import { useState, useEffect } from 'react';
import { fetchApi } from '../lib/api';
import { exportToCsv } from '../lib/csvExport';
import AdminDetailDrawer from '../components/admin/AdminDetailDrawer';
import CompanyDetail from '../components/admin/CompanyDetail';
import InventorDetail from '../components/admin/InventorDetail';
import NDARecordDetail from '../components/admin/NDARecordDetail';

export default function AdminDashboard() {
  const [tab, setTab] = useState('event-settings');
  const [pendingCompanies, setPendingCompanies] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [allInventors, setAllInventors] = useState([]);
  const [allConnections, setAllConnections] = useState([]);
  const [allInvites, setAllInvites] = useState([]);
  const [allNdaRecords, setAllNdaRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectingId, setRejectingId] = useState(null);

  // [EVENT-MODE] Event settings state
  const [eventSettings, setEventSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  // Detail drawer state
  const [drawer, setDrawer] = useState(null); // { type, id, title, data }

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pending, companies, inventors, connections, invites, ndas, settings] = await Promise.all([
        fetchApi('/api/admin/pending-companies'),
        fetchApi('/api/admin/companies'),
        fetchApi('/api/admin/inventors'),
        fetchApi('/api/admin/connections'),
        fetchApi('/api/admin/invites'),
        fetchApi('/api/admin/nda-records'),
        fetchApi('/api/event-settings'),
      ]);
      setPendingCompanies(pending.companies || []);
      setAllCompanies(companies.companies || []);
      setAllInventors(inventors.inventors || []);
      setAllConnections(connections.connections || []);
      setAllInvites(invites.invites || []);
      setAllNdaRecords(ndas.records || []);
      setEventSettings(settings.settings || null);
    } catch (err) {
      console.error('Admin load error:', err);
    } finally {
      setLoading(false);
    }
  };

  // [EVENT-MODE] Save event settings
  const saveEventSettings = async () => {
    if (!eventSettings) return;
    setSavingSettings(true);
    try {
      const res = await fetchApi('/api/event-settings', {
        method: 'PUT',
        body: JSON.stringify(eventSettings),
      });
      setEventSettings(res.settings);
    } catch (err) {
      alert('Failed to save event settings: ' + err.message);
    } finally {
      setSavingSettings(false);
    }
  };

  // [EVENT-MODE] Toggle user access
  const toggleUserAccess = async (userId, currentAccess) => {
    try {
      await fetchApi(`/api/admin/users/${userId}/access`, {
        method: 'PUT',
        body: JSON.stringify({ event_access_granted: !currentAccess }),
      });
      await loadData();
    } catch (err) {
      alert('Failed to toggle access: ' + err.message);
    }
  };

  const approveCompany = async (id) => {
    try {
      await fetchApi(`/api/admin/companies/${id}/approve`, { method: 'PUT' });
      await loadData();
    } catch (err) { alert('Approve failed: ' + err.message); }
  };

  const rejectCompany = async (id) => {
    try {
      await fetchApi(`/api/admin/companies/${id}/reject`, {
        method: 'PUT',
        body: JSON.stringify({ reason: rejectionReason }),
      });
      setRejectingId(null);
      setRejectionReason('');
      await loadData();
    } catch (err) { alert('Reject failed: ' + err.message); }
  };

  const updateConnectionStatus = async (id, status) => {
    try {
      await fetchApi(`/api/admin/connections/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
      await loadData();
    } catch (err) { alert('Update failed: ' + err.message); }
  };

  // Filter helper
  const matchSearch = (str) => !search || (str || '').toLowerCase().includes(search.toLowerCase());

  const tabs = [
    { key: 'event-settings', label: 'Event Settings' },
    { key: 'pending', label: `Pending (${pendingCompanies.length})` },
    { key: 'companies', label: `Companies (${allCompanies.length})` },
    { key: 'inventors', label: `Inventors (${allInventors.length})` },
    { key: 'connections', label: `Connections (${allConnections.length})` },
    { key: 'invites', label: `Invites (${allInvites.length})` },
    { key: 'ndas', label: `NDA Records (${allNdaRecords.length})` },
  ];

  if (loading) return <div className="text-sm text-gray-400 p-4">Loading admin data...</div>;

  const thCls = "pb-3 pr-4 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider";
  const tdCls = "py-3 pr-4";
  const rowCls = "cursor-pointer hover:bg-gray-50/50 transition-colors";

  return (
    <div>
      <h1 className="font-heading text-3xl text-vault-navy mb-6">Admin Dashboard</h1>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-btn p-1 w-fit overflow-x-auto">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setSearch(''); }}
            className={`py-2 px-4 rounded-[10px] text-sm font-semibold transition-all whitespace-nowrap ${
              tab === t.key ? 'bg-white text-vault-navy shadow-btn' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search + Export bar */}
      {tab !== 'pending' && (
        <div className="flex items-center gap-3 mb-5">
          <input
            className="flex-1 max-w-sm px-4 py-2.5 rounded-input border-2 border-vault-border text-sm text-vault-navy bg-white/70 focus:border-vault-gold focus:outline-none"
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button
            onClick={() => {
              const exportMap = {
                companies: { data: allCompanies, name: 'companies' },
                inventors: { data: allInventors, name: 'inventors' },
                connections: { data: allConnections, name: 'connections' },
                invites: { data: allInvites, name: 'invites' },
                ndas: { data: allNdaRecords, name: 'nda-records' },
              };
              const exp = exportMap[tab];
              if (exp) exportToCsv(exp.data, exp.name);
            }}
            className="px-4 py-2.5 rounded-btn text-sm font-semibold border-2 border-vault-border text-vault-navy hover:bg-gray-50"
          >
            Export CSV
          </button>
        </div>
      )}

      {/* ========== EVENT SETTINGS ========== */}
      {tab === 'event-settings' && eventSettings && (
        <div className="space-y-5 max-w-2xl">
          <div className="glass p-7">
            <h2 className="font-heading text-xl text-vault-navy mb-5">Event Configuration</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Event Name</label>
                <input
                  className="w-full px-4 py-2.5 rounded-input border-2 border-vault-border text-sm text-vault-navy bg-white/70 focus:border-vault-gold focus:outline-none"
                  value={eventSettings.event_name || ''}
                  onChange={e => setEventSettings(s => ({ ...s, event_name: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Event Start</label>
                  <input
                    type="datetime-local"
                    className="w-full px-4 py-2.5 rounded-input border-2 border-vault-border text-sm text-vault-navy bg-white/70 focus:border-vault-gold focus:outline-none"
                    value={eventSettings.event_start ? eventSettings.event_start.slice(0, 16) : ''}
                    onChange={e => setEventSettings(s => ({ ...s, event_start: e.target.value ? new Date(e.target.value).toISOString() : null }))}
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Event End</label>
                  <input
                    type="datetime-local"
                    className="w-full px-4 py-2.5 rounded-input border-2 border-vault-border text-sm text-vault-navy bg-white/70 focus:border-vault-gold focus:outline-none"
                    value={eventSettings.event_end ? eventSettings.event_end.slice(0, 16) : ''}
                    onChange={e => setEventSettings(s => ({ ...s, event_end: e.target.value ? new Date(e.target.value).toISOString() : null }))}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 py-2">
                <button
                  onClick={() => setEventSettings(s => ({ ...s, submissions_open: !s.submissions_open }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${eventSettings.submissions_open ? 'bg-emerald-500' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${eventSettings.submissions_open ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className="text-sm text-vault-navy font-medium">Inventor Submissions Open</span>
                <span className="text-xs text-gray-400">(Independent of event window)</span>
              </div>
              <button
                onClick={saveEventSettings}
                disabled={savingSettings}
                className="px-6 py-2.5 rounded-btn text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
              >
                {savingSettings ? 'Saving...' : 'Save Event Settings'}
              </button>
            </div>
          </div>

          {/* User Access Management */}
          <div className="glass p-7">
            <h2 className="font-heading text-xl text-vault-navy mb-2">User Access</h2>
            <p className="text-sm text-gray-400 mb-5">Toggle access on/off for each user. Users with access can use the app during the event window.</p>

            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Inventors ({allInventors.length})</h3>
            <div className="space-y-2 mb-6">
              {allInventors.map(inv => (
                <div key={inv.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50">
                  <div>
                    <div className="text-sm font-medium text-vault-navy">{inv.display_name}</div>
                    <div className="text-xs text-gray-400">{inv.email}</div>
                  </div>
                  <button
                    onClick={() => toggleUserAccess(inv.id, inv.event_access_granted)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${inv.event_access_granted ? 'bg-emerald-500' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${inv.event_access_granted ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              ))}
              {allInventors.length === 0 && <p className="text-sm text-gray-400">No inventors registered yet.</p>}
            </div>

            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Companies ({allCompanies.length})</h3>
            <div className="space-y-2">
              {allCompanies.map(co => (
                <div key={co.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50">
                  <div>
                    <div className="text-sm font-medium text-vault-navy">{co.company_profile?.company_name || co.display_name}</div>
                    <div className="text-xs text-gray-400">{co.email}</div>
                  </div>
                  <button
                    onClick={() => toggleUserAccess(co.id, co.event_access_granted)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${co.event_access_granted ? 'bg-emerald-500' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${co.event_access_granted ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              ))}
              {allCompanies.length === 0 && <p className="text-sm text-gray-400">No companies registered yet.</p>}
            </div>
          </div>
        </div>
      )}

      {/* ========== PENDING ========== */}
      {tab === 'pending' && (
        <div className="space-y-4">
          {pendingCompanies.length === 0 ? (
            <div className="glass p-7 text-center"><p className="text-sm text-gray-400">No pending applications.</p></div>
          ) : pendingCompanies.map(c => (
            <div key={c.id} className="glass p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 cursor-pointer" onClick={() => setDrawer({ type: 'company', id: c.id, title: c.company_profile?.company_name || c.display_name })}>
                  <div className="flex items-center gap-3 mb-2">
                    {c.company_profile?.logo_url && <img src={c.company_profile.logo_url} alt="" className="w-10 h-10 rounded-btn object-cover" />}
                    <h3 className="font-semibold text-vault-navy text-base">{c.company_profile?.company_name || c.display_name}</h3>
                  </div>
                  <div className="space-y-1 text-sm text-gray-500">
                    <div><span className="text-gray-400">Email:</span> {c.email}</div>
                    {c.company_profile?.company_type && <div><span className="text-gray-400">Type:</span> <span className="capitalize">{c.company_profile.company_type}</span></div>}
                    {c.company_profile?.website && <div><span className="text-gray-400">Website:</span> <a href={c.company_profile.website} target="_blank" rel="noreferrer" className="text-vault-gold hover:underline">{c.company_profile.website}</a></div>}
                    {c.company_profile?.categories?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {c.company_profile.categories.map((cat, i) => <span key={i} className="px-2 py-0.5 bg-gray-100 rounded-full text-[10px] text-gray-500">{cat}</span>)}
                      </div>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-400 mt-2">Applied {new Date(c.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button onClick={() => approveCompany(c.id)} className="px-5 py-2 rounded-btn text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}>Approve</button>
                  {rejectingId === c.id ? (
                    <div className="space-y-2">
                      <textarea className="w-full px-3 py-2 rounded-input border-2 border-vault-border text-xs" placeholder="Rejection reason..." value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} rows={2} />
                      <div className="flex gap-2">
                        <button onClick={() => rejectCompany(c.id)} className="px-3 py-1.5 rounded-btn text-xs font-semibold text-vault-error border border-vault-error/30">Confirm</button>
                        <button onClick={() => { setRejectingId(null); setRejectionReason(''); }} className="px-3 py-1.5 rounded-btn text-xs text-gray-400">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setRejectingId(c.id)} className="px-5 py-2 rounded-btn text-sm font-semibold text-vault-error border border-vault-error/30 hover:bg-red-50">Reject</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ========== COMPANIES ========== */}
      {tab === 'companies' && (
        <div className="glass p-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr>
              <th className={thCls}>Company</th>
              <th className={thCls}>Type</th>
              <th className={thCls}>Status</th>
              <th className={thCls}>Inventors</th>
              <th className={thCls}>Email</th>
              <th className={thCls}>Approved</th>
              <th className={thCls}>Joined</th>
            </tr></thead>
            <tbody className="divide-y divide-vault-border">
              {allCompanies
                .filter(c => matchSearch(c.company_profile?.company_name) || matchSearch(c.email) || matchSearch(c.display_name))
                .map(c => (
                <tr key={c.id} className={rowCls} onClick={() => setDrawer({ type: 'company', id: c.id, title: c.company_profile?.company_name || c.display_name })}>
                  <td className={tdCls}>
                    <div className="flex items-center gap-2">
                      {c.company_profile?.logo_url && <img src={c.company_profile.logo_url} alt="" className="w-7 h-7 rounded object-cover" />}
                      <span className="font-medium text-vault-navy">{c.company_profile?.company_name || c.display_name}</span>
                    </div>
                  </td>
                  <td className={tdCls + " text-gray-500 capitalize"}>{c.company_profile?.company_type || '—'}</td>
                  <td className={tdCls}>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${c.role === 'company' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                      {c.role === 'company' ? 'Approved' : 'Pending'}
                    </span>
                  </td>
                  <td className={tdCls + " text-gray-400"}>{allConnections.filter(conn => conn.company?.company_name === c.company_profile?.company_name && conn.status === 'approved').length}</td>
                  <td className={tdCls + " text-gray-400"}>{c.email}</td>
                  <td className={tdCls + " text-gray-400"}>{c.company_profile?.approved_at ? new Date(c.company_profile.approved_at).toLocaleDateString() : '—'}</td>
                  <td className={tdCls + " text-gray-400"}>{new Date(c.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ========== INVENTORS ========== */}
      {tab === 'inventors' && (
        <div className="glass p-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr>
              <th className={thCls}>Name</th>
              <th className={thCls}>Email</th>
              <th className={thCls}>Invited By</th>
              <th className={thCls}>Connections</th>
              <th className={thCls}>Joined</th>
            </tr></thead>
            <tbody className="divide-y divide-vault-border">
              {allInventors
                .filter(inv => matchSearch(inv.display_name) || matchSearch(inv.email))
                .map(inv => {
                  const invCompanyId = inv.inventor_profile?.invited_by_company_id;
                  const invitingCo = invCompanyId ? allCompanies.find(c => c.id === invCompanyId) : null;
                  const connCount = allConnections.filter(c => c.inventor?.display_name === inv.display_name).length;
                  return (
                    <tr key={inv.id} className={rowCls} onClick={() => setDrawer({ type: 'inventor', id: inv.id, title: inv.display_name })}>
                      <td className={tdCls + " font-medium text-vault-navy"}>{inv.display_name}</td>
                      <td className={tdCls + " text-gray-400"}>{inv.email}</td>
                      <td className={tdCls + " text-gray-500"}>{invitingCo?.company_profile?.company_name || (invCompanyId ? 'Unknown' : '—')}</td>
                      <td className={tdCls + " text-gray-400"}>{connCount}</td>
                      <td className={tdCls + " text-gray-400"}>{new Date(inv.created_at).toLocaleDateString()}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {/* ========== CONNECTIONS ========== */}
      {tab === 'connections' && (
        <div className="glass p-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr>
              <th className={thCls}>Inventor</th>
              <th className={thCls}>Company</th>
              <th className={thCls}>Status</th>
              <th className={thCls}>Type</th>
              <th className={thCls}>NDA</th>
              <th className={thCls}>Vouched By</th>
              <th className={thCls}>Date</th>
            </tr></thead>
            <tbody className="divide-y divide-vault-border">
              {allConnections
                .filter(c => matchSearch(c.inventor?.display_name) || matchSearch(c.company?.company_name))
                .map(conn => (
                <tr key={conn.id} className={rowCls} onClick={() => {
                  // Find NDA records for this connection
                  const ndas = allNdaRecords.filter(n => n.connection_id === conn.id);
                  setDrawer({ type: 'connection-ndas', id: conn.id, title: `${conn.inventor?.display_name} ↔ ${conn.company?.company_name}`, data: { connection: conn, ndas } });
                }}>
                  <td className={tdCls + " font-medium text-vault-navy"}>{conn.inventor?.display_name || '—'}</td>
                  <td className={tdCls + " text-gray-500"}>{conn.company?.company_name || '—'}</td>
                  <td className={tdCls}>
                    <select
                      className="px-2 py-1 rounded text-[11px] font-medium border border-vault-border bg-white"
                      value={conn.status}
                      onClick={e => e.stopPropagation()}
                      onChange={e => updateConnectionStatus(conn.id, e.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="declined">Declined</option>
                    </select>
                  </td>
                  <td className={tdCls + " text-gray-400 capitalize"}>{conn.connection_type}</td>
                  <td className={tdCls + " text-gray-400"}>{conn.nda_agreed ? `v${conn.nda_version_agreed} — ${new Date(conn.nda_agreed_at).toLocaleDateString()}` : 'No'}</td>
                  <td className={tdCls + " text-gray-400"}>{conn.voucher?.company_name || '—'}</td>
                  <td className={tdCls + " text-gray-400"}>{new Date(conn.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ========== INVITES ========== */}
      {tab === 'invites' && (
        <div className="glass p-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr>
              <th className={thCls}>From Company</th>
              <th className={thCls}>To Email</th>
              <th className={thCls}>Status</th>
              <th className={thCls}>Claimed By</th>
              <th className={thCls}>Expires</th>
              <th className={thCls}>Created</th>
            </tr></thead>
            <tbody className="divide-y divide-vault-border">
              {allInvites
                .filter(inv => matchSearch(inv.company?.company_name) || matchSearch(inv.invited_email) || matchSearch(inv.claimed_inventor?.display_name))
                .map(inv => (
                <tr key={inv.id}>
                  <td className={tdCls + " font-medium text-vault-navy"}>{inv.company?.company_name || '—'}</td>
                  <td className={tdCls + " text-gray-400"}>{inv.invited_email || 'Generic link'}</td>
                  <td className={tdCls}>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      inv.used_at ? 'bg-emerald-50 text-emerald-700' :
                      new Date(inv.expires_at) < new Date() ? 'bg-red-50 text-red-600' :
                      'bg-blue-50 text-blue-700'
                    }`}>
                      {inv.used_at ? 'Claimed' : new Date(inv.expires_at) < new Date() ? 'Expired' : 'Active'}
                    </span>
                  </td>
                  <td className={tdCls + " text-gray-400"}>{inv.claimed_inventor?.display_name || '—'}</td>
                  <td className={tdCls + " text-gray-400"}>{new Date(inv.expires_at).toLocaleDateString()}</td>
                  <td className={tdCls + " text-gray-400"}>{new Date(inv.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ========== NDA RECORDS ========== */}
      {tab === 'ndas' && (
        <div className="glass p-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr>
              <th className={thCls}>Inventor</th>
              <th className={thCls}>Company</th>
              <th className={thCls}>Inventor Signed</th>
              <th className={thCls}>Company Action</th>
              <th className={thCls}>Version</th>
              <th className={thCls}>Document</th>
              <th className={thCls}>Date</th>
            </tr></thead>
            <tbody className="divide-y divide-vault-border">
              {allNdaRecords
                .filter(n => matchSearch(n.connection?.inventor?.display_name) || matchSearch(n.connection?.company?.company_name) || matchSearch(n.inventor_signature_text))
                .map(nda => (
                <tr key={nda.id} className={rowCls} onClick={() => setDrawer({ type: 'nda', id: nda.id, title: `NDA Record`, data: nda })}>
                  <td className={tdCls + " font-medium text-vault-navy"}>{nda.connection?.inventor?.display_name || '—'}</td>
                  <td className={tdCls + " text-gray-500"}>{nda.connection?.company?.company_name || '—'}</td>
                  <td className={tdCls}>
                    {nda.inventor_signed_at ? (
                      <div>
                        <div className="text-vault-navy text-xs">{nda.inventor_signature_text}</div>
                        <div className="text-[10px] text-gray-400">{new Date(nda.inventor_signed_at).toLocaleString()}</div>
                        <div className="text-[10px] text-gray-400 font-mono">IP: {nda.inventor_ip_address}</div>
                      </div>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className={tdCls}>
                    {nda.company_action_at ? (
                      <div>
                        <div className="text-vault-navy text-xs">{nda.company_signature_text}</div>
                        <div className="text-[10px] text-gray-400">{nda.company_action_type === 'countersigned' ? 'Countersigned' : 'Acknowledged'}</div>
                        <div className="text-[10px] text-gray-400">{new Date(nda.company_action_at).toLocaleString()}</div>
                        <div className="text-[10px] text-gray-400 font-mono">IP: {nda.company_ip_address}</div>
                      </div>
                    ) : <span className="text-amber-600 text-xs font-medium">Pending</span>}
                  </td>
                  <td className={tdCls + " text-gray-400"}>v{nda.nda_version}</td>
                  <td className={tdCls}>
                    {nda.document_url && (
                      <a href={nda.document_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="text-vault-gold text-xs font-semibold hover:underline">
                        View
                      </a>
                    )}
                  </td>
                  <td className={tdCls + " text-gray-400"}>{new Date(nda.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ========== DETAIL DRAWER ========== */}
      {drawer && drawer.type === 'company' && (
        <AdminDetailDrawer title={drawer.title} onClose={() => setDrawer(null)}>
          <CompanyDetail companyId={drawer.id} onUpdate={loadData} />
        </AdminDetailDrawer>
      )}

      {drawer && drawer.type === 'inventor' && (
        <AdminDetailDrawer title={drawer.title} onClose={() => setDrawer(null)}>
          <InventorDetail inventorId={drawer.id} onUpdate={loadData} />
        </AdminDetailDrawer>
      )}

      {drawer && drawer.type === 'nda' && (
        <AdminDetailDrawer title={drawer.title} onClose={() => setDrawer(null)}>
          <NDARecordDetail record={drawer.data} />
        </AdminDetailDrawer>
      )}

      {drawer && drawer.type === 'connection-ndas' && (
        <AdminDetailDrawer title={drawer.title} onClose={() => setDrawer(null)}>
          <div className="space-y-6">
            <div className="text-sm space-y-1">
              <div><span className="text-gray-400">Status:</span> <span className="text-vault-navy font-medium capitalize">{drawer.data.connection.status}</span></div>
              <div><span className="text-gray-400">Type:</span> <span className="text-vault-navy capitalize">{drawer.data.connection.connection_type}</span></div>
              <div><span className="text-gray-400">NDA Agreed:</span> <span className="text-vault-navy">{drawer.data.connection.nda_agreed ? `Yes (v${drawer.data.connection.nda_version_agreed})` : 'No'}</span></div>
            </div>
            <h3 className="font-semibold text-vault-navy">NDA Audit Trail ({drawer.data.ndas.length} records)</h3>
            {drawer.data.ndas.length === 0 ? (
              <p className="text-sm text-gray-400">No NDA records for this connection.</p>
            ) : (
              drawer.data.ndas.map(nda => (
                <div key={nda.id} className="border border-vault-border rounded-btn p-4">
                  <NDARecordDetail record={nda} />
                </div>
              ))
            )}
          </div>
        </AdminDetailDrawer>
      )}
    </div>
  );
}
